import { InfoNotice, InfoNoticeAction } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useOptionalRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { useRefreshFsnIncomingOrderMutation } from '@/lib/services/fsn-network/fsn-network.query'
import type {
  FsnIncomingOrderChangeAttention,
  SalesOrder,
} from '@/lib/services/sales-orders/sales-orders.types'
import { getErrorMessage, readProperty } from '@/lib/utils'
import { toast } from 'sonner'

type FsnChangeState = NonNullable<FsnIncomingOrderChangeAttention['state']>

const changeMessages: Record<FsnChangeState, string> = {
  open: 'Review the requested changes before dispatching this order.',
  responding: 'We’re waiting for FSN to confirm your response.',
  response_unknown: 'FSN hasn’t confirmed your response. Check its status before dispatching.',
  needs_finish: 'FSN accepted changes to this order. Finish updating it before dispatching.',
  fsn_updated:
    'FSN changed this order and it already shows the changes. Review them and mark them as seen.',
}

/** `change` is the source link's `fsn_change`: the workflow's change state and typed sync issue. */
export function FsnOrderStatusNotice({
  change,
}: {
  change?: FsnIncomingOrderChangeAttention | null
}) {
  const path = usePathHelper()
  const orgId = useOptionalRouteOrganizationId()
  if (!change) return null
  return (
    <InfoNotice
      tone="warning"
      title={
        change.state === 'fsn_updated'
          ? 'FSN updated this order'
          : change.state
            ? 'FSN changes need attention'
            : 'FSN status needs attention'
      }
      action={
        <InfoNoticeAction
          {...path.wireIn
            .index()
            .using({ orgId })
            .search({ incomingOrderId: change.incoming_order_id }).link}
        >
          Review FSN order
        </InfoNoticeAction>
      }
    >
      {(change.state && changeMessages[change.state]) ?? change.issue?.message}
    </InfoNotice>
  )
}

interface FsnCancelOrderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  order: Pick<SalesOrder, 'id' | 'reference_id' | 'order_source_link'>
}

export function FsnCancelOrderDialog({ open, onOpenChange, order }: FsnCancelOrderDialogProps) {
  const refresh = useRefreshFsnIncomingOrderMutation()
  const link = order.order_source_link
  const incomingOrderId = readProperty(link?.metadata, 'fsn_incoming_order_id')
  const syncError = link?.fsn_change?.issue?.message
  const reference = link?.external_reference
  const checkStatus = () => {
    if (typeof incomingOrderId !== 'number' && typeof incomingOrderId !== 'string') return
    refresh.mutate(
      { incomingOrderId },
      {
        onSuccess: () => toast.success('FSN status checked.'),
        onError: (error) => toast.error(getErrorMessage(error) ?? 'Unable to check FSN status.'),
      }
    )
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel FSN order</DialogTitle>
          <DialogDescription>
            Contact Flower Shop Network to cancel this order. Once FSN confirms cancellation, we
            will cancel unfinished work in OneStem. If a driver is already out, contact them too.
          </DialogDescription>
        </DialogHeader>
        <Typography variant="description">
          OneStem order {order.reference_id || order.id} · FSN {reference}
        </Typography>
        {syncError ? <InfoNotice tone="warning">{syncError}</InfoNotice> : null}
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <a
              href={`https://admin.flowershopnetwork.com/relay/orders/${encodeURIComponent(String(reference))}/`}
              target="_blank"
              rel="noreferrer"
            >
              Open order in FSN
            </a>
          </Button>
          <Button disabled={refresh.isPending || !incomingOrderId} onClick={checkStatus}>
            Check FSN status
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
